import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { useToast } from "./useToast";
import { downloadGeneratedPdf, requestDocumentGeneration, waitForCompletedDocument } from "./useGeneratedDocument";
import type { HerstelSubmission, HerstelSubmissionUpsert, InspectieType } from "../../../../types";

/** Noun used in the user-facing messages of this composable. */
const SUBJECT = "het formulier";

/**
 * Herstelverklaring state for one rapport + inspectie type: loading/saving the
 * herstel_submissions row (installateur gegevens, handtekening/upload, formal
 * submission) and downloading the printable installateur form generated by the
 * printer service via the documents queue.
 */
export function useHerstelSubmission() {
  const submission = ref<HerstelSubmission | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const generatingForm = ref(false);
  const error = ref<string | null>(null);
  const toast = useToast();

  async function loadSubmission(rapportId: string, inspectieType: InspectieType): Promise<HerstelSubmission | null> {
    loading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await supabase
        .from("herstel_submissions")
        .select("*")
        .eq("rapport_id", rapportId)
        .eq("inspectie_type", inspectieType)
        .maybeSingle();

      if (fetchError) throw fetchError;
      submission.value = (data as unknown as HerstelSubmission | null) ?? null;
      return submission.value;
    } catch (err) {
      console.error("Error loading herstel submission:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij laden van de herstelverklaring";
      toast.error("De herstelverklaring kon niet geladen worden. Herlaad de pagina of probeer het later opnieuw.", err);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function saveSubmission(payload: HerstelSubmissionUpsert): Promise<HerstelSubmission | null> {
    saving.value = true;
    error.value = null;

    try {
      const { data, error: upsertError } = await supabase
        .from("herstel_submissions")
        .upsert(payload as never, { onConflict: "rapport_id,inspectie_type" })
        .select("*")
        .single();

      if (upsertError) throw upsertError;
      submission.value = data as unknown as HerstelSubmission;
      return submission.value;
    } catch (err) {
      console.error("Error saving herstel submission:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij opslaan van de herstelverklaring";
      toast.error("Opslaan van de herstelverklaring is mislukt. Probeer het opnieuw.", err);
      return null;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Generate the printable installateur form (printer service, documents queue)
   * and trigger a browser download once the PDF is ready.
   */
  async function downloadHerstelFormulier(
    rapportId: string,
    inspectieType: InspectieType,
    fileName: string,
  ): Promise<void> {
    generatingForm.value = true;
    error.value = null;

    try {
      const documentId = await requestDocumentGeneration({
        type: `herstelformulier_${inspectieType}`,
        rapportId,
        subject: SUBJECT,
      });

      const document = await waitForCompletedDocument(documentId, {
        subject: SUBJECT,
        onSlow: () => toast.info("Het formulier wordt gegenereerd, dit kan even duren..."),
      });

      await downloadGeneratedPdf(document.pdf_path, fileName, SUBJECT);
    } catch (err) {
      console.error("Error downloading herstelformulier:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij downloaden van het formulier";
      throw err;
    } finally {
      generatingForm.value = false;
    }
  }

  return {
    submission,
    loading,
    saving,
    generatingForm,
    error,
    loadSubmission,
    saveSubmission,
    downloadHerstelFormulier,
  };
}
